import React, { useState, useEffect } from 'react';
import { Box, Button, useTheme, useMediaQuery } from '@mui/material';
import axios from 'axios';

interface IdeaPreset {
  id: string;
  name: string;
  shortName?: string;
  data: {
    topic: string;
    brand: string;
    brandDescription?: string;
    sector: string;
    sectorId?: string;
    platform: string;
    preferredIcpTitle?: string; // Will be mapped to actual ICP ID
  };
}

interface SimulationPreset {
  id: string;
  name: string;
  shortName?: string;
  data: {
    content: string;
    brand: string;
    brandDescription?: string;
    sector: string;
    sectorId?: string;
    platform: string;
  };
}

interface FormPresetsProps {
  mode: 'ideas' | 'simulation';
  onSelect: (data: any) => void;
  selectedId?: string;
}

// Base preset data without sectorIds (will be populated dynamically)
const ideaPresetsBase: IdeaPreset[] = [
  {
    id: 'product-launch',
    name: 'Product Launch',
    shortName: 'Product',
    data: {
      topic: 'New product announcement with key features and benefits',
      brand: 'TechStartup',
      brandDescription: 'Innovative B2B SaaS platform providing AI-powered marketing analytics and automation tools for enterprise clients',
      sector: 'Marketing Technology', // Using exact sector name from DB
      platform: 'LinkedIn',
      preferredIcpTitle: 'Tech Enthusiasts',
    },
  },
  {
    id: 'industry-insights',
    name: 'Industry Insights',
    shortName: 'Insights',
    data: {
      topic: 'Market trends and analysis for Q1 2025',
      brand: 'ConsultCo',
      brandDescription: 'Leading business intelligence consultancy specializing in data-driven insights and strategic planning for Fortune 500 companies',
      sector: 'BI & Dashboards', // Will use a general business sector
      platform: 'LinkedIn',
      preferredIcpTitle: 'Business Professionals',
    },
  },
  {
    id: 'creative-campaign',
    name: 'Creative Campaign',
    shortName: 'Creative',
    data: {
      topic: 'Brand storytelling campaign for new audience',
      brand: 'CreativeAgency',
      brandDescription: 'Award-winning creative agency crafting authentic brand experiences through innovative storytelling and cutting-edge design',
      sector: 'Advertising & Marketing Agencies', // Will use marketing sector
      platform: 'Instagram',
      preferredIcpTitle: 'Creative Millennials',
    },
  },
];

const simulationPresetsBase: SimulationPreset[] = [
  {
    id: 'corporate-update',
    name: 'Corporate Update',
    shortName: 'Corporate',
    data: {
      content: 'Excited to announce our Q4 results exceeded expectations with 35% YoY growth. Our team\'s dedication and innovative approach have driven remarkable success. Looking forward to an even stronger 2025! #BusinessGrowth #Success',
      brand: 'CorpFinance',
      brandDescription: 'Digital-first financial services company offering personalized investment advice and wealth management solutions to millennials and Gen Z',
      sector: 'Personal Finance', // Using exact sector name from DB search results
      platform: 'LinkedIn',
    },
  },
  {
    id: 'event-announcement',
    name: 'Event Announcement',
    shortName: 'Event',
    data: {
      content: 'Join us this weekend for our annual community festival! Live music, local vendors, and family activities from 10am-6pm at Central Park. Free admission and parking. See you there!',
      brand: 'EventOrg',
      brandDescription: 'Community-focused event planning organization bringing people together through memorable local experiences and cultural celebrations',
      sector: 'Events & Conferences', // Will use events sector
      platform: 'Facebook',
    },
  },
  {
    id: 'thought-leadership',
    name: 'Thought Leadership',
    shortName: 'Thought',
    data: {
      content: 'Here\'s what we learned from analyzing 1000+ successful campaigns: Authenticity beats perfection every time. Your audience wants to connect with real stories, not polished facades. Thread below',
      brand: 'ThoughtLeader',
      brandDescription: 'Executive education platform helping business leaders develop critical thinking skills through evidence-based research and peer collaboration',
      sector: 'Education - Higher Ed', // Using exact sector name from DB search results
      platform: 'Twitter/X',
    },
  },
];

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

const FormPresets: React.FC<FormPresetsProps> = ({ mode, onSelect, selectedId }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [ideaPresets, setIdeaPresets] = useState<IdeaPreset[]>(ideaPresetsBase);
  const [simulationPresets, setSimulationPresets] = useState<SimulationPreset[]>(simulationPresetsBase);
  
  // Fetch sectors on mount to map names to IDs
  useEffect(() => {
    const fetchSectorMapping = async () => {
      try {
        // Fetch sectors for all preset sector names
        const sectorNames = [
          ...new Set([
            ...ideaPresetsBase.map(p => p.data.sector),
            ...simulationPresetsBase.map(p => p.data.sector)
          ])
        ];
        
        const mapping: Record<string, string> = {};
        
        for (const sectorName of sectorNames) {
          try {
            // Try to search for each sector
            const response = await axios.get(`${API_URL}/api/v1/sectors/search`, {
              params: { query: sectorName.split(' ')[0] } // Use first word for search
            });
            
            if (response.data?.sectors?.length > 0) {
              // Try to find exact match first
              const exactMatch = response.data.sectors.find(
                (s: any) => s.name.toLowerCase() === sectorName.toLowerCase()
              );
              
              if (exactMatch) {
                mapping[sectorName] = exactMatch.id;
              } else {
                // Use first result as fallback
                mapping[sectorName] = response.data.sectors[0].id;
              }
            }
          } catch (err) {
            console.warn(`Could not fetch sector mapping for: ${sectorName}`);
          }
        }
        
        
        // Update presets with sector IDs
        setIdeaPresets(ideaPresetsBase.map(preset => ({
          ...preset,
          data: {
            ...preset.data,
            sectorId: mapping[preset.data.sector] || undefined
          }
        })));
        
        setSimulationPresets(simulationPresetsBase.map(preset => ({
          ...preset,
          data: {
            ...preset.data,
            sectorId: mapping[preset.data.sector] || undefined
          }
        })));
      } catch (error) {
        console.error('Error fetching sector mappings:', error);
      }
    };
    
    fetchSectorMapping();
  }, []);
  
  const presets = mode === 'ideas' ? ideaPresets : simulationPresets;

  return (
    <Box sx={{ mb: { xs: 1.5, sm: 2 } }}>
      <Box 
        sx={{ 
          display: 'flex', 
          flexWrap: 'wrap',
          gap: { xs: 0.5, sm: 0.75 },
          justifyContent: 'center',
        }}
      >
        {presets.map((preset) => (
          <Button
            key={preset.id}
            onClick={() => onSelect(preset.data)}
            size="small"
            sx={{
              background: 'rgba(10, 124, 160, 0.2)',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              border: '1px solid rgba(10, 124, 160, 0.3)',
              color: 'rgba(255, 255, 255, 0.85)',
              fontSize: { xs: '0.7rem', sm: '0.8rem' },
              fontWeight: 500,
              py: { xs: 0.5, sm: 0.6 },
              px: { xs: 1.2, sm: 1.75 },
              borderRadius: '16px',
              textTransform: 'none',
              minHeight: 'auto',
              transition: 'all 0.3s ease',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
              '&:hover': {
                background: 'rgba(10, 124, 160, 0.3)',
                color: 'white',
                borderColor: 'rgba(10, 124, 160, 0.4)',
                transform: 'translateY(-1px)',
                boxShadow: '0 3px 6px rgba(0, 0, 0, 0.3)',
              },
              ...(selectedId === preset.id && {
                background: 'rgba(10, 124, 160, 0.5)',
                color: 'white',
                borderColor: '#0a7ca0',
                boxShadow: '0 2px 8px rgba(10, 124, 160, 0.4)',
              }),
            }}
          >
            {isMobile && preset.shortName ? `+ ${preset.shortName}` : `+ ${preset.name}`}
          </Button>
        ))}
      </Box>
    </Box>
  );
};

export default FormPresets;